import * as THREE from 'three';
import { params } from '../config.js';
import * as SceneModule from '../scene.js';
import {
  airplaneState,
  keysPressed,
  mouseControl,
  gyroControl,
  touchControl,
  getAirplaneMaterials
} from './state.js';
import { getAirplaneGroup } from './create.js';
import { state, scrollOffset } from '../terrain/tiling.js';
import { sampleTerrainHeight } from '../terrain/heightmap.js';
import { updateTiltIndicator, updateGyroStatus } from '../controls/gyro.js';
import { pointMaterial } from '../terrain/material.js';
import { triggerBoostGlitch } from '../effects/glitch.js';
import { triggerSonicBoom } from '../effects/sonicBoom.js';

// Reusable objects for update loop (avoid GC pressure)
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _camForward = new THREE.Vector3();
const _camRight = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();
const _nosePosition = new THREE.Vector3();

// Reusable colors (avoid creating every frame)
const _dangerColor = new THREE.Color(0xff0000);   // red when colliding
const _safeColor = new THREE.Color(0xff9000);     // yellow when safe (matches HUD)
const _landedColor = new THREE.Color(0x4ade80);   // green when landed

// Start hold-to-launch charging
export function startLaunchCharge() {
  if (airplaneState.flightState !== 'landed') return;
  if (airplaneState.launchCharging) return; // already charging
  airplaneState.launchCharging = true;
  airplaneState.launchCharge = 0;

}

// Cancel hold-to-launch (W released early)
export function cancelLaunchCharge() {
  if (!airplaneState.launchCharging) return;
  airplaneState.launchCharging = false;
  airplaneState.launchCharge = 0;

}

// Execute the actual launch (called when charge is full)
function executeLaunch() {
  airplaneState.launchCharging = false;
  airplaneState.launchCharge = 0;

  // Smooth lift off - set target higher but nudge current y up slightly
  // so the lerp eases into it gradually instead of bouncing
  airplaneState.y += 0.15;
  airplaneState.targetY = airplaneState.y + 0.6;

  // Clear collision state
  airplaneState.collisionShake = 0;
  airplaneState.isColliding = false;

  // Solid forward thrust so the airplane doesn't stall
  state.scrollSpeed = 3.0;
  params.scrollSpeed = 3.0;
  
  // Reset flight state
  airplaneState.flightState = 'flying';
  
  // Longer launch immunity to let the airplane gain altitude
  airplaneState.launchImmunityTimer = 1.0;
}

// Update launch charge (called every frame from updateAirplane)
function updateLaunchCharge(delta) {
  if (!airplaneState.launchCharging) return;

  airplaneState.launchCharge += delta / airplaneState.launchChargeDuration;

  // Fully charged → launch
  if (airplaneState.launchCharge >= 1) {
    executeLaunch();
  }
}

// Restart function - reset after crash
export function restartAirplane() {
  if (airplaneState.flightState !== 'crashed') return;

  // Hide crash overlay
  const crashOverlay = document.getElementById('crash-overlay');
  if (crashOverlay) crashOverlay.classList.remove('visible');

  // Reset airplane position and state
  airplaneState.heading = 0;
  airplaneState.targetHeading = 0;
  airplaneState.y = 0;
  airplaneState.targetY = 0;
  airplaneState.tilt = 0;
  airplaneState.pitch = 0;
  airplaneState.strafeOffset = 0;
  airplaneState.targetStrafeOffset = 0;
  airplaneState.collisionShake = 0;
  airplaneState.worldPos.set(0, airplaneState.baseAltitude, 0);

  // Reset terrain scroll to move to a "new" area
  scrollOffset.x = 0;
  scrollOffset.y = 0;

  // Set initial speed
  state.scrollSpeed = 3.0;
  params.scrollSpeed = 3.0;
  
  // Reset flight state
  airplaneState.flightState = 'flying';
}

export function updateAirplane(delta) {
  const { camera } = SceneModule;
  const airplaneGroup = getAirplaneGroup();
  if (!airplaneGroup || !airplaneState.enabled) return;

  const isChaseMode = params.cameraMode === 'chase';
  const isTopdownMode = params.cameraMode === 'topdown';

  // ========== PROCESS GYRO INPUT ==========
  if (gyroControl.enabled && gyroControl.active) {
    // Handle manual calibration request (recalibrate button)
    if (gyroControl.needsCalibration) {
      gyroControl.neutralBeta = gyroControl.rawBeta;
      gyroControl.neutralGamma = gyroControl.rawGamma;
      gyroControl.calibrated = true;
      gyroControl.needsCalibration = false;
      console.log('Gyro manually calibrated:', { beta: gyroControl.neutralBeta, gamma: gyroControl.neutralGamma });
    }

    // Calculate delta from neutral position
    let deltaBeta = gyroControl.rawBeta - gyroControl.neutralBeta;   // pitch (climb/dive)
    let deltaGamma = gyroControl.rawGamma - gyroControl.neutralGamma; // roll (turn)
    
    // Apply dead zone (smaller for more responsiveness)
    const deadZone = 2; // degrees
    if (Math.abs(deltaBeta) < deadZone) deltaBeta = 0;
    if (Math.abs(deltaGamma) < deadZone) deltaGamma = 0;

    // Normalize to -1 to 1 range
    // gamma: positive = tilted right, so positive deltaGamma = turn right
    // beta: positive = tilted forward, so positive deltaBeta = nose down (dive)
    const maxAngle = 25; // degrees for full deflection (reduced for sensitivity)
    const rawTurnRate = Math.max(-1, Math.min(1, deltaGamma / maxAngle));
    const rawClimbRate = Math.max(-1, Math.min(1, deltaBeta / maxAngle)); // positive: tilt forward = climb

    // Smooth the input (higher value = more responsive)
    const smoothFactor = 0.3;
    gyroControl.smoothTurnRate += (rawTurnRate - gyroControl.smoothTurnRate) * smoothFactor;
    gyroControl.smoothClimbRate += (rawClimbRate - gyroControl.smoothClimbRate) * smoothFactor;

    gyroControl.turnRate = gyroControl.smoothTurnRate;
    gyroControl.climbRate = gyroControl.smoothClimbRate;

    // Update tilt indicator visual
    updateTiltIndicator(gyroControl.turnRate, gyroControl.climbRate);

    // Debug: update status with control values
    const t = gyroControl.turnRate.toFixed(2);
    const c = gyroControl.climbRate.toFixed(2);
    updateGyroStatus(true, `T:${t} C:${c}`);
  }

  // ========== HANDLE THROTTLE INPUT ==========
  // Keyboard (W/S) or Touch zones control speed (only when flying)
  const throttleSpeed = 3.0; // speed change per second
  const maxSpeed = 10.0;
  const minSpeed = 0.0;

  if (airplaneState.flightState === 'flying') {
    // Throttle up: keyboard W or touch zone
    // Don't cap at maxSpeed if currently boosting (boost allows going higher)
    if (keysPressed.throttleUp || touchControl.throttleUp) {
      const speedCap = airplaneState.isBoosting ? (maxSpeed + 5.0) : maxSpeed;
      state.scrollSpeed = Math.min(speedCap, state.scrollSpeed + throttleSpeed * delta);
      params.scrollSpeed = state.scrollSpeed;
    }
    // Throttle down: keyboard S or touch zone
    if (keysPressed.throttleDown || touchControl.throttleDown) {
      state.scrollSpeed = Math.max(minSpeed, state.scrollSpeed - throttleSpeed * delta);
      params.scrollSpeed = state.scrollSpeed;
    }

    // ========== BOOST SYSTEM LOGIC ==========
    // New passive charging system: charge fills automatically, press shift to use
    const boostChargeSpeed = 0.35;
    const boostMaxSpeed = maxSpeed + 5.0; // Boost to 15.0

    // Calculate ground clearance for boost multiplier (reuse for collision detection later)
    const terrainHeight = sampleTerrainHeight(airplaneGroup.position.x, airplaneGroup.position.z);
    if (terrainHeight !== null) {
      airplaneState.groundClearance = airplaneGroup.position.y - terrainHeight;
    } else {
      airplaneState.groundClearance = 10.0; // No terrain = high clearance
    }

    // Check if dust wake conditions are met (low altitude + high speed)
    // This triggers the 3x boost charge multiplier
    airplaneState.isLowAltitudeBoost = params.dustWakeEnabled &&
      state.scrollSpeed >= params.dustWakeSpeedThreshold &&
      airplaneState.groundClearance > 0 &&
      airplaneState.groundClearance <= params.dustWakeClearanceMax;

    // Passive charging: charge increases when not boosting
    // Apply 3x multiplier when flying low and fast (dust wake active)
    if (!airplaneState.isBoosting) {
      const boostMultiplier = airplaneState.isLowAltitudeBoost ? 3.0 : 1.0;
      airplaneState.boostCharge = Math.min(1.0, airplaneState.boostCharge + boostChargeSpeed * boostMultiplier * delta);
    }

    // Instant boost activation: press shift to use accumulated charge
    if (airplaneState.shiftJustPressed && !airplaneState.isBoosting && airplaneState.boostCharge > 0.1) {
      // Store original charge before depletion (for sonic boom check)
      const originalBoostCharge = airplaneState.boostCharge;
      
      // Activate boost with duration proportional to charge level
      airplaneState.isBoosting = true;
      airplaneState.boostTimeRemaining = airplaneState.boostCharge * airplaneState.boostMaxDuration;
      airplaneState.boostCharge = 0; // Deplete charge on activation
      
      // Mark that we want FOV punch (will ramp up smoothly, not snap)
      airplaneState.boostFovPunchTarget = 1.0;
      
      // Trigger glitch effect
      triggerBoostGlitch();
      
      // Trigger sonic boom effect if at max speed, flying straight, boost meter was full, and cooldown has passed
      if (state.scrollSpeed >= maxSpeed && originalBoostCharge >= 0.99) {
        // Check if flying straight (low tilt and pitch angles)
        // Tilt is roll angle (banking), pitch is nose up/down
        // Threshold: 0.15 radians ≈ 8.6 degrees - must be very straight
        const maxTilt = 0.15;
        const maxPitch = 0.15;
        const isFlyingStraight = Math.abs(airplaneState.tilt) < maxTilt && Math.abs(airplaneState.pitch) < maxPitch;
        
        if (isFlyingStraight) {
          // Calculate forward direction for sonic boom orientation
          // Forward direction is calculated later in the function, so we calculate it here too
          _forward.set(
            Math.sin(airplaneState.heading),
            0,
            -Math.cos(airplaneState.heading)
          );
          
          // Calculate nose position: airplane position + forward offset
          // Nose is at local (0, 0, 0.5) scaled by airplaneState.size
          const noseOffset = 0.5 * airplaneState.size;
          _nosePosition.copy(airplaneGroup.position);
          _nosePosition.addScaledVector(_forward, noseOffset);
          
          // Get current time for cooldown tracking
          const currentTime = performance.now() * 0.001; // Convert to seconds
          
          // Trigger sonic boom at nose position
          triggerSonicBoom(_nosePosition, _forward, currentTime);
        }
      }
    }
    // Clear the just-pressed flag after processing
    airplaneState.shiftJustPressed = false;

    if (airplaneState.isBoosting) {
      // Only drain boost while shift is held - release to conserve remaining boost
      if (airplaneState.isShiftPressed) {
        const drainMultiplier = 0.7;
        airplaneState.boostTimeRemaining -= delta * drainMultiplier;

        // Smoothly ramp to boost speed
        const rampSpeed = throttleSpeed * 4; // Faster ramp for boost
        state.scrollSpeed = Math.min(boostMaxSpeed, state.scrollSpeed + rampSpeed * delta);
        params.scrollSpeed = state.scrollSpeed;

        if (airplaneState.boostTimeRemaining <= 0) {
          airplaneState.isBoosting = false;
          airplaneState.boostTimeRemaining = 0;
        }
      } else {
        // Shift released - stop boosting and preserve remaining charge
        airplaneState.isBoosting = false;
        // Convert remaining time back to charge (so gauge shows what's left)
        airplaneState.boostCharge = airplaneState.boostTimeRemaining / airplaneState.boostMaxDuration;
        airplaneState.boostTimeRemaining = 0;
      }
    } else if (state.scrollSpeed > maxSpeed) {
      // Ease back to normal max speed after boost
      state.scrollSpeed = Math.max(maxSpeed, state.scrollSpeed - throttleSpeed * 2 * delta);
      params.scrollSpeed = state.scrollSpeed;
    }

    // Dynamic FOV and Mask Radius based on speed
    const speedFactor = state.scrollSpeed / maxSpeed;
    
    // Smooth the FOV factor for a "pressed into seat" feeling
    // FOV lags behind actual speed changes
    const fovDiff = speedFactor - airplaneState.smoothFovFactor;
    airplaneState.smoothFovFactor += fovDiff * Math.min(1, params.fovEasing * delta);

    // ========== FOV PUNCH EFFECT ==========
    // Smoothly ramp FOV punch toward target (avoids snapping on re-trigger)
    const punchRampUpSpeed = 4.0;  // How fast to ramp UP to target (higher = faster)
    const punchDecaySpeed = 1.5;   // How fast to decay when target is 0
    
    // Set target to 0 when not actively boosting (will decay smoothly)
    if (!airplaneState.isBoosting) {
      airplaneState.boostFovPunchTarget = 0;
    }
    
    // Initialize target if undefined
    if (airplaneState.boostFovPunchTarget === undefined) {
      airplaneState.boostFovPunchTarget = 0;
    }
    
    // Smoothly ramp toward target
    const punchDiff = airplaneState.boostFovPunchTarget - airplaneState.boostFovPunch;
    if (punchDiff > 0) {
      // Ramping up toward target
      airplaneState.boostFovPunch = Math.min(
        airplaneState.boostFovPunchTarget,
        airplaneState.boostFovPunch + punchRampUpSpeed * delta
      );
    } else if (punchDiff < 0) {
      // Decaying down toward target (slower)
      airplaneState.boostFovPunch = Math.max(
        airplaneState.boostFovPunchTarget,
        airplaneState.boostFovPunch - punchDecaySpeed * delta
      );
    }
    
    // Apply easeOutQuad to the punch value for smooth feel
    const punchT = airplaneState.boostFovPunch;
    const punchIntensity = punchT * (2 - punchT); // easeOutQuad
    const punchFovBonus = punchIntensity * 18; // Up to 18 degrees extra FOV at peak

    // Update FOV using smoothed factor + punch effect
    const baseFov = params.minFov + (params.maxFov - params.minFov) * airplaneState.smoothFovFactor;
    const targetFov = baseFov + punchFovBonus;
    camera.fov = targetFov;
    camera.updateProjectionMatrix();
    params.camFov = targetFov;

    // Update Mask Radius with smoothing (uses same smoothed factor as FOV to prevent snapping)
    const targetMaskRadius = params.minMaskRadius + (params.maxMaskRadius - params.minMaskRadius) * airplaneState.smoothFovFactor;
    params.maskRadius = targetMaskRadius;
    pointMaterial.uniforms.uMaskRadius.value = targetMaskRadius;

    // Handle strafe input - A/D or touch zones control lateral movement
    if (keysPressed.strafeLeft || touchControl.strafeLeft) {
      airplaneState.targetStrafeOffset = Math.max(-airplaneState.maxStrafe,
        airplaneState.targetStrafeOffset - airplaneState.strafeSpeed * delta);
    }
    if (keysPressed.strafeRight || touchControl.strafeRight) {
      airplaneState.targetStrafeOffset = Math.min(airplaneState.maxStrafe,
        airplaneState.targetStrafeOffset + airplaneState.strafeSpeed * delta);
    }
  }
  // Ease strafe offset toward target (heavy ease-in)
  const strafeDiff = airplaneState.targetStrafeOffset - airplaneState.strafeOffset;
  airplaneState.strafeOffset += strafeDiff * Math.min(1, airplaneState.strafeEasing * delta);

  // ========== HANDLE STEERING INPUT ==========
  // Determine active turn/climb rates from mouse OR gyro
  let activeTurnRate = 0;
  let activeClimbRate = 0;
  let hasSteeringInput = false;

  // Gyro takes priority on mobile if active
  if (gyroControl.enabled && gyroControl.active && airplaneState.flightState === 'flying') {
    activeTurnRate = gyroControl.turnRate;
    activeClimbRate = gyroControl.climbRate;
    hasSteeringInput = true;
  }
  // Fall back to mouse on desktop
  else if (mouseControl.active && airplaneState.flightState === 'flying') {
    activeTurnRate = mouseControl.turnRate;
    activeClimbRate = mouseControl.climbRate;
    hasSteeringInput = true;
  }

  // Apply steering if we have input
  if (hasSteeringInput) {
    // Turn rate: position determines how fast we turn
    const baseTurnSpeed = 2.0; // radians per second at full deflection (increased)
    const turnSpeed = state.scrollSpeed > 0 ? baseTurnSpeed : baseTurnSpeed * 0.5;
    airplaneState.heading += activeTurnRate * turnSpeed * delta;

    // Climb rate: controls altitude change
    const baseClimbSpeed = 4.0; // units per second at full deflection (increased)
    const climbSpeed = state.scrollSpeed > 0 ? baseClimbSpeed : baseClimbSpeed * 0.5;
    airplaneState.targetY += activeClimbRate * climbSpeed * delta;
    airplaneState.targetY = Math.max(-airplaneState.altitudeRange,
      Math.min(airplaneState.altitudeRange, airplaneState.targetY));
  }

  // Smooth vertical movement toward target
  const dy = airplaneState.targetY - airplaneState.y;
  airplaneState.y += dy * Math.min(1, airplaneState.smoothing * delta);

  // Calculate tilt based on turning (bank into turns like real airplane)
  const tiltAmount = state.scrollSpeed > 0 ? 0.5 : 0.15;
  const targetTilt = activeTurnRate * tiltAmount;
  airplaneState.tilt += (targetTilt - airplaneState.tilt) * Math.min(1, 6 * delta);

  // Calculate pitch based on vertical movement
  const targetPitch = dy * 0.8; // nose up when climbing, down when diving
  airplaneState.pitch += (targetPitch - airplaneState.pitch) * Math.min(1, 6 * delta);

  // Get forward direction based on heading (heading 0 = -Z direction)
  _forward.set(
    Math.sin(airplaneState.heading),
    0,
    -Math.cos(airplaneState.heading)
  );

  // Get right vector
  _right.crossVectors(_forward, _up).normalize();
  
  if (isChaseMode || isTopdownMode) {
    // Free roam mode: airplane stays at origin, faces heading direction
    // Terrain scrolls based on heading and speed
    // Strafe offset is perpendicular to heading
    const strafeX = Math.cos(airplaneState.heading) * airplaneState.strafeOffset;
    const strafeZ = Math.sin(airplaneState.heading) * airplaneState.strafeOffset;

    airplaneState.worldPos.x = strafeX;
    airplaneState.worldPos.z = strafeZ;
    airplaneState.worldPos.y = airplaneState.baseAltitude + airplaneState.y;

    // Set airplane position from world pos
    airplaneGroup.position.copy(airplaneState.worldPos);
  } else {
    // Normal mode: airplane positioned relative to camera
    camera.getWorldDirection(_camForward);
    _camForward.y = 0;
    if (_camForward.lengthSq() < 1e-6) _camForward.set(0, 0, -1);
    _camForward.normalize();
    
    _camRight.crossVectors(_camForward, _up).normalize();
    
    airplaneGroup.position.copy(camera.position);
    airplaneGroup.position.addScaledVector(_camForward, airplaneState.distance);
    airplaneGroup.position.y = camera.position.y + airplaneState.height + airplaneState.y;
  }

  // Update scale
  airplaneGroup.scale.set(airplaneState.size, airplaneState.size, airplaneState.size);

  // Orient airplane to face heading direction with tilt and pitch
  _lookTarget.copy(airplaneGroup.position).add(_forward);
  airplaneGroup.lookAt(_lookTarget);
  airplaneGroup.rotation.z = airplaneState.tilt;
  airplaneGroup.rotation.x += airplaneState.pitch;

  const time = performance.now() * 0.001;

  // Decrement launch immunity timer
  if (airplaneState.launchImmunityTimer > 0) {
    airplaneState.launchImmunityTimer -= delta;
  }

  // Collision detection - sample actual terrain height at airplane position
  const terrainHeight = sampleTerrainHeight(airplaneGroup.position.x, airplaneGroup.position.z);
  let surfaceHeight = terrainHeight;

  const wasColliding = airplaneState.isColliding;
  
  // Skip collision detection during launch immunity
  if (airplaneState.launchImmunityTimer > 0) {
    airplaneState.isColliding = false;
  } else if (terrainHeight !== null) {
    // Compare airplane Y to surface height at this position
    const clearance = airplaneGroup.position.y - surfaceHeight;

    // Touchdown should trigger as soon as we reach the surface (prevents sinking underground)
    const touchdownEpsilon = 0.05;
    airplaneState.isColliding =
      (clearance <= touchdownEpsilon) ||
      (clearance < airplaneState.collisionThreshold);
  } else {
    airplaneState.isColliding = false; // No heightmap yet
  }

  // Handle collision effects
  // Airplane uses fixed UI colors (yellow/red), separate from ground highlight
  
  // Get UI overlay elements
  const crashOverlay = document.getElementById('crash-overlay');

  // Get airplane materials
  const airplaneMaterials = getAirplaneMaterials();

  // Only process new collisions when in flying state
  if (airplaneState.isColliding && airplaneState.flightState === 'flying') {
    // Determine if this is a crash or landing based on speed
    if (state.scrollSpeed <= airplaneState.crashSpeedThreshold) {
      // Landing - low/no speed
      airplaneState.flightState = 'landed';
      state.scrollSpeed = 0;
      params.scrollSpeed = 0;

      // Snap to the surface height so we rest on top of the terrain / sell pad.
      if (surfaceHeight !== null) {
        const targetY = surfaceHeight + 0.001; // tiny lift to avoid z-fighting
        const dySnap = targetY - airplaneGroup.position.y;
        airplaneGroup.position.y = targetY;
        // Keep state in sync so the next frame doesn't pull us back underground.
        airplaneState.y += dySnap;
        airplaneState.targetY = airplaneState.y;
      }

      // Freeze any residual strafe drift while landed
      airplaneState.targetStrafeOffset = airplaneState.strafeOffset;
      
      // Show landing circle based on whether we're in a sell zone
      if (crashOverlay) crashOverlay.classList.remove('visible');
      
      // Turn airplane to landed color (green)
      for (const mat of airplaneMaterials) {
        mat.color.copy(_landedColor);
      }
    } else {
      // Crash - too fast!
      airplaneState.flightState = 'crashed';
      state.scrollSpeed = 0;
      params.scrollSpeed = 0;
      
      // Show crash overlay
      if (crashOverlay) crashOverlay.classList.add('visible');
    }
  }

  // Handle visual effects based on flight state
  if (airplaneState.flightState === 'crashed') {
    // Increase shake intensity for crash
    airplaneState.collisionShake = Math.min(airplaneState.collisionShake + delta * 15, 1.0);

    // Turn airplane to danger color
    for (const mat of airplaneMaterials) {
      mat.color.copy(_dangerColor);
    }

    // Apply intense shake
    const shakeIntensity = airplaneState.collisionShake * 0.08;
    airplaneGroup.position.x += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.y += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.z += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.rotation.z += (Math.random() - 0.5) * shakeIntensity * 0.5;
  } else if (airplaneState.flightState === 'landed') {
    // Landed state - airplane stays still, green color
    airplaneState.collisionShake = 0;
    for (const mat of airplaneMaterials) {
      mat.color.copy(_landedColor);
    }
    // Update hold-to-launch charge
    updateLaunchCharge(delta);
  } else if (airplaneState.isColliding) {
    // Flying but scraping terrain - warning shake
    airplaneState.collisionShake = Math.min(airplaneState.collisionShake + delta * 15, 1.0);

    // Turn airplane to danger color
    for (const mat of airplaneMaterials) {
      mat.color.copy(_dangerColor);
    }

    // Apply shake
    const shakeIntensity = airplaneState.collisionShake * 0.08;
    airplaneGroup.position.x += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.y += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.z += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.rotation.z += (Math.random() - 0.5) * shakeIntensity * 0.5;
  } else {
    // Decay shake when not colliding
    airplaneState.collisionShake = Math.max(airplaneState.collisionShake - delta * 5, 0);

    // Transition to safe color smoothly
    for (const mat of airplaneMaterials) {
      const currentColor = mat.color;
      currentColor.r += (_safeColor.r - currentColor.r) * delta * 5;
      currentColor.g += (_safeColor.g - currentColor.g) * delta * 5;
      currentColor.b += (_safeColor.b - currentColor.b) * delta * 5;
    }

    // Add subtle bobbing motion only when not colliding
    airplaneGroup.position.y += Math.sin(time * 2) * 0.02;
  }
}

// Setup crash overlay click handler for mobile
export function setupCrashOverlayHandler() {
  const crashOverlayEl = document.getElementById('crash-overlay');
  if (crashOverlayEl) {
    crashOverlayEl.addEventListener('click', () => {
      if (airplaneState.flightState === 'crashed') {
        restartAirplane();
      }
    });
  }
}
